import { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Copy, Loader2, ShieldCheck, Wallet, Clock, ChevronRight, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import CoinIcon from "@/components/CoinIcon";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import api, { API, apiErr } from "@/lib/api";
import { formatTRY, formatCrypto, formatCrypto as fc, truncate } from "@/lib/format";

function useCountdown(expiresAt) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => {
      const diff = new Date(expiresAt).getTime() - Date.now();
      setLeft(Math.max(0, Math.floor(diff / 1000)));
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [expiresAt]);
  const m = String(Math.floor(left / 60)).padStart(2, "0");
  const s = String(left % 60).padStart(2, "0");
  return { left, text: `${m}:${s}` };
}

export default function Checkout() {
  const { paymentId } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coins, setCoins] = useState([]);
  const [selCoin, setSelCoin] = useState(null);
  const [selNet, setSelNet] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const pollRef = useRef();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/payments/${paymentId}`);
      setPayment(data);
    } catch (e) {
      setError(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, [paymentId]);

  useEffect(() => {
    load();
    api.get("/coins").then(({ data }) => setCoins(data.coins)).catch(() => {});
  }, [load]);

  const status = payment?.status;
  const hasAddress = payment?.pay_address && ["waiting", "detected", "confirming", "confirmed"].includes(status);

  // poll status
  useEffect(() => {
    if (!payment) return;
    if (["confirmed", "expired", "failed", "cancelled"].includes(status)) return;
    if (!payment.coin) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/${paymentId}/status`);
        if (data.status !== status) {
          setPayment((p) => ({ ...p, status: data.status, tx_hash: data.tx_hash || p.tx_hash }));
        }
      } catch {}
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [payment, status, paymentId]);

  const doSelect = async () => {
    if (!selCoin || !selNet) return;
    setSelecting(true);
    try {
      const { data } = await api.post(`/payments/${paymentId}/select`, { coin: selCoin, network: selNet });
      setPayment(data);
      toast.success("Ödeme adresi oluşturuldu.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSelecting(false);
    }
  };

  const copyAddr = () => {
    navigator.clipboard.writeText(payment.pay_address);
    toast.success("Adres kopyalandı.");
  };

  const cd = useCountdown(payment?.expires_at);

  if (loading) return <CheckoutShell><div className="space-y-4"><Skeleton className="h-8 w-32" /><Skeleton className="h-40 w-full" /><Skeleton className="h-12 w-full" /></div></CheckoutShell>;
  if (error) return <CheckoutShell><div className="text-center py-8"><AlertCircle className="mx-auto text-red-500" size={40} /><p className="mt-4 font-semibold">{error}</p></div></CheckoutShell>;

  const storeName = payment.store_name || "Coinbuy Mağaza";
  const showSelector = !payment.coin && ["created"].includes(status);

  return (
    <CheckoutShell storeName={storeName} logo={payment.logo_url}>
      {/* amount header */}
      <div className="text-center pb-5 border-b border-border">
        <p className="text-xs text-muted-foreground">Toplam Tutar</p>
        <p className="mt-1 text-3xl font-extrabold tracking-tighter" data-testid="checkout-amount">{formatTRY(payment.amount_try)}</p>
        {payment.crypto_amount && (
          <p className="mt-1 text-sm font-medium text-primary" data-testid="checkout-crypto">
            ≈ {formatCrypto(payment.crypto_amount, payment.coin)}
          </p>
        )}
        {payment.order_id && <p className="mt-2 text-xs text-muted-foreground">Sipariş No: {payment.order_id}</p>}
        {payment.description && <p className="text-xs text-muted-foreground">{payment.description}</p>}
        <div className="mt-3 flex justify-center"><StatusBadge status={status} /></div>
      </div>

      <AnimatePresence mode="wait">
        {/* Coin selector */}
        {showSelector && (
          <motion.div key="selector" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pt-5 space-y-4">
            <div>
              <p className="text-sm font-semibold mb-2.5">Kripto Para Seçin</p>
              <div className="grid grid-cols-1 gap-2" data-testid="coin-selector">
                {coins.map((c) => (
                  <button
                    key={c.symbol}
                    data-testid={`coin-option-${c.symbol}`}
                    onClick={() => { setSelCoin(c.symbol); setSelNet(c.networks[0].code); }}
                    className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-colors ${selCoin === c.symbol ? "border-primary bg-accent" : "border-border hover:border-primary/40"}`}
                  >
                    <CoinIcon symbol={c.symbol} size={34} />
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.symbol} · 1 {c.symbol} ≈ {formatTRY(c.rate_try)}</p>
                    </div>
                    {selCoin === c.symbol && <Check size={18} className="text-primary" />}
                  </button>
                ))}
              </div>
            </div>

            {selCoin && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}>
                <p className="text-sm font-semibold mb-2.5">Ağ Seçin</p>
                <div className="flex flex-wrap gap-2" data-testid="network-selector">
                  {coins.find((c) => c.symbol === selCoin).networks.map((n) => (
                    <button
                      key={n.code}
                      data-testid={`network-option-${n.code}`}
                      onClick={() => setSelNet(n.code)}
                      className={`rounded-full border px-3.5 py-2 text-xs font-medium transition-colors ${selNet === n.code ? "border-primary bg-primary text-white" : "border-border hover:border-primary/40"}`}
                    >
                      {n.label}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}

            <Button onClick={doSelect} disabled={!selCoin || !selNet || selecting} data-testid="checkout-continue-btn" className="w-full rounded-full h-11">
              {selecting ? <Loader2 className="animate-spin" size={17} /> : <>Devam Et <ChevronRight size={17} /></>}
            </Button>
          </motion.div>
        )}

        {/* Payment (address + QR) */}
        {hasAddress && status !== "confirmed" && (
          <motion.div key="pay" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="pt-5 space-y-4">
            <div className="flex items-center justify-center gap-2">
              <CoinIcon symbol={payment.coin} size={22} />
              <span className="text-sm font-semibold">{payment.coin}</span>
              <span className="rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">{payment.network}</span>
            </div>

            {status === "waiting" && (
              <>
                <div className="mx-auto h-44 w-44 rounded-xl border border-border bg-white p-3">
                  <img src={`${API}/qr?data=${encodeURIComponent(payment.pay_address)}`} alt="QR" className="h-full w-full" data-testid="checkout-qr" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground text-center mb-1.5">Bu adrese <span className="font-semibold text-foreground">{formatCrypto(payment.crypto_amount, payment.coin)}</span> gönderin</p>
                  <button onClick={copyAddr} data-testid="copy-address-btn" className="w-full flex items-center gap-2 rounded-xl border border-border bg-muted px-3 py-2.5 hover:border-primary/40 transition-colors">
                    <span className="flex-1 text-left text-xs font-mono text-muted-foreground truncate">{payment.pay_address}</span>
                    <Copy size={15} className="text-primary shrink-0" />
                  </button>
                </div>
              </>
            )}

            <StatusFlowVisual status={status} />

            {payment.expires_at && cd.left > 0 && status === "waiting" && (
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground" data-testid="checkout-countdown">
                <Clock size={15} /> Kalan süre: <span className="font-semibold text-foreground tabular-nums">{cd.text}</span>
              </div>
            )}
          </motion.div>
        )}

        {/* Success */}
        {status === "confirmed" && (
          <motion.div key="success" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="pt-6 text-center" data-testid="checkout-success">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 240, damping: 16 }} className="mx-auto h-20 w-20 rounded-full bg-emerald-500 grid place-items-center shadow-[0_10px_30px_rgba(16,185,129,0.4)]">
              <Check size={40} strokeWidth={3} className="text-white" />
            </motion.div>
            <h2 className="mt-4 text-xl font-extrabold tracking-tighter">Ödeme Başarılı</h2>
            <div className="mt-5 space-y-2 rounded-xl border border-border bg-muted/40 p-4 text-left text-sm">
              <Row l="Tutar" r={formatTRY(payment.amount_try)} />
              <Row l="Kripto" r={formatCrypto(payment.crypto_amount, payment.coin)} />
              <Row l="Ödeme No" r={truncate(payment.id, 10)} />
              {payment.tx_hash && <Row l="İşlem Hash" r={truncate(payment.tx_hash, 8)} />}
            </div>
          </motion.div>
        )}

        {/* Failed / Expired */}
        {(status === "expired" || status === "failed") && (
          <motion.div key="failed" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="pt-6 text-center" data-testid="checkout-failed">
            <div className="mx-auto h-16 w-16 rounded-full bg-red-500/10 grid place-items-center">
              <AlertCircle size={32} className="text-red-500" />
            </div>
            <h2 className="mt-4 text-lg font-bold">{status === "expired" ? "Ödeme Süresi Doldu" : "Ödeme Başarısız"}</h2>
            <p className="mt-1 text-sm text-muted-foreground">Lütfen mağaza ile iletişime geçin.</p>
          </motion.div>
        )}
      </AnimatePresence>
    </CheckoutShell>
  );
}

const FLOW = [
  { key: "waiting", label: "Ödeme Bekleniyor" },
  { key: "detected", label: "Ödeme Algılandı" },
  { key: "confirming", label: "Blockchain Doğrulanıyor" },
  { key: "confirmed", label: "Ödeme Onaylandı" },
];

function StatusFlowVisual({ status }) {
  const order = ["waiting", "detected", "confirming", "confirmed"];
  const cur = order.indexOf(status);
  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-2.5">
      {FLOW.map((f, i) => {
        const done = i < cur;
        const active = i === cur;
        return (
          <div key={f.key} className="flex items-center gap-3">
            <span className={`grid h-6 w-6 place-items-center rounded-full text-white transition-colors ${done ? "bg-emerald-500" : active ? "bg-primary" : "bg-muted-foreground/20"}`}>
              {done ? <Check size={13} strokeWidth={3} /> : active ? <Loader2 size={13} className="animate-spin" /> : <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50" />}
            </span>
            <span className={`text-sm ${active ? "font-semibold" : done ? "text-muted-foreground" : "text-muted-foreground/60"}`}>{f.label}</span>
          </div>
        );
      })}
    </div>
  );
}

function Row({ l, r }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{l}</span>
      <span className="font-medium font-mono">{r}</span>
    </div>
  );
}

function CheckoutShell({ children, storeName, logo }) {
  return (
    <div className="min-h-screen bg-muted/30 grid-bg flex flex-col">
      <div className="mx-auto w-full max-w-md flex items-center justify-between px-4 py-4">
        <Logo />
        <ThemeToggle />
      </div>
      <div className="flex-1 flex items-start justify-center px-4 pb-10">
        <div className="w-full max-w-md">
          {storeName && (
            <div className="mb-4 flex items-center gap-3 rounded-2xl border border-border bg-card p-4">
              {logo ? <img src={logo} alt="" className="h-10 w-10 rounded-lg object-cover" /> : <div className="h-10 w-10 rounded-lg bg-primary/10 grid place-items-center"><Wallet size={18} className="text-primary" /></div>}
              <div>
                <p className="text-xs text-muted-foreground">Mağaza</p>
                <p className="text-sm font-bold">{storeName}</p>
              </div>
            </div>
          )}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-[0_12px_40px_rgba(0,0,0,0.08)]">
            {children}
          </div>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck size={13} /> Coinbuy ile güvenli ödeme
          </p>
        </div>
      </div>
    </div>
  );
}
